import { Divider, Icon, IconName, Stack, Text } from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { NavigationItem } from '../../../tokens/navigationPaths';
import { MobileNavMenuFooter } from './MobileNavMenuFooter';
import { DEVICE_TYPE_ICONS, getMenuRows, isActivePath, isMobileSupportedSection } from './MobileNavMenuUtils';
import {
  CurrentSectionButton,
  GroupLabel,
  PickerRow,
  SheetFooter,
  SheetHeader,
  SheetNavItems,
  SheetWrapper,
} from './styles';

export function MobileNavMenuSheet({
  currentSection,
  topNavItems,
  closeModal,
}: {
  currentSection: NavigationItem | undefined;
  topNavItems: NavigationItem[];
  closeModal: () => void;
}) {
  const [showSections, setShowSections] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const rows = useMemo(() => getMenuRows(currentSection), [currentSection]);

  const handleNavigate = useCallback(
    (item: NavigationItem) => {
      if (item.path == null) {
        return;
      }

      navigate(item.path);
      closeModal();
    },
    [closeModal, navigate]
  );

  return (
    <SheetWrapper data-testid="mobile-nav-menu-sheet">
      <SheetHeader>
        <CurrentSectionButton
          onClick={() => setShowSections(value => !value)}
          data-testid="mobile-nav-menu-current-section"
          startIcon={currentSection?.icon}
          endIcon={showSections ? IconName.ChevronUp : IconName.ChevronDown}
        >
          {currentSection?.label}
        </CurrentSectionButton>

        <Divider color="backgroundDivider" />
      </SheetHeader>

      <SheetNavItems>
        {showSections ? (
          <Stack gap="spacing4">
            {topNavItems.map(item => (
              <PickerRow
                key={item.path ?? item.label}
                onClick={() => handleNavigate(item)}
                data-testid="mobile-nav-menu-section-row"
                startIcon={item.icon}
                endIcon={!isMobileSupportedSection(item) ? IconName.DeviceDesktop : undefined}
                isActive={isActivePath(item.path, pathname)}
              >
                {item.label}
              </PickerRow>
            ))}
          </Stack>
        ) : (
          <Stack gap="spacing4">
            {rows.map(row =>
              row.type === 'group' ? (
                <GroupLabel key={row.key}>{row.label}</GroupLabel>
              ) : (
                <PickerRow
                  key={row.key}
                  type="button"
                  onClick={() => handleNavigate(row.item)}
                  data-testid="mobile-nav-menu-submenu-row"
                  isActive={isActivePath(row.item.path, pathname)}
                  ghost
                >
                  <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {row.item.label}
                  </Text>
                  {row.item.deviceType != null && row.item.deviceType !== 'all' && (
                    <Icon icon={DEVICE_TYPE_ICONS[row.item.deviceType]} size={16} />
                  )}
                </PickerRow>
              )
            )}
          </Stack>
        )}
      </SheetNavItems>

      <SheetFooter>
        <MobileNavMenuFooter />
      </SheetFooter>
    </SheetWrapper>
  );
}
